import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';

type FormState = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  message: string;
};

export default function ContactPage() {
  const [form, setForm] = useState<FormState>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const token = useAuthStore((state) => state.token);

  function validate() {
    const e: Partial<FormState> = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.lastName.trim()) e.lastName = 'Last name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.message.trim()) e.message = 'Please enter a message';
    if (form.phone && !/^[0-9+\-()\s]{6,}$/.test(form.phone)) e.phone = 'Enter a valid phone';
    return e;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSuccess(null);
    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation).length) return;

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to send message');
      setSuccess('Thank you — your message has been sent. A confirmation email was sent to your address.');
      setForm({ firstName: '', lastName: '', phone: '', email: '', message: '' });
    } catch (err) {
      setSuccess(String((err as Error).message || 'Something went wrong. Please try again later.'));
    } finally {
      setIsSubmitting(false);
    }
  }

  const BRAND = '#fd9e10';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fff8ee] via-[#fffaf3] to-white flex flex-col relative overflow-hidden">
      <style>{`
        @keyframes dz-float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(20px, -30px) scale(1.08); }
        }
        @keyframes dz-float-slow {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-25px, 25px) scale(1.05); }
        }
        @keyframes dz-fade-up {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes dz-shimmer {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes dz-pop-in {
          0% { opacity: 0; transform: scale(0.96) translateY(6px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes dz-spin {
          to { transform: rotate(360deg); }
        }
        .dz-blob-1 { animation: dz-float 9s ease-in-out infinite; }
        .dz-blob-2 { animation: dz-float-slow 11s ease-in-out infinite; }
        .dz-blob-3 { animation: dz-float 13s ease-in-out infinite; }
        .dz-fade-up { opacity: 0; animation: dz-fade-up 0.7s ease-out forwards; }
        .dz-pop-in { animation: dz-pop-in 0.4s ease-out; }
        .dz-shimmer-btn {
          background-size: 200% 100%;
          animation: dz-shimmer 3s linear infinite;
        }
        .dz-spinner {
          animation: dz-spin 0.7s linear infinite;
        }
        .dz-input {
          transition: box-shadow 0.25s ease, border-color 0.25s ease, transform 0.15s ease;
        }
        .dz-input:focus {
          transform: translateY(-1px);
        }
      `}</style>

      {/* Ambient animated gradient blobs */}
      <div className="dz-blob-1 pointer-events-none absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-gradient-to-br from-[#fd9e10]/30 via-[#ffb74d]/20 to-transparent blur-3xl" />
      <div className="dz-blob-2 pointer-events-none absolute top-1/3 -right-40 w-[32rem] h-[32rem] rounded-full bg-gradient-to-bl from-[#fd9e10]/25 via-[#ffd08a]/20 to-transparent blur-3xl" />
      <div className="dz-blob-3 pointer-events-none absolute bottom-0 left-1/4 w-[24rem] h-[24rem] rounded-full bg-gradient-to-tr from-[#ffe3b3]/40 to-transparent blur-3xl" />

      <main className="flex-1 w-full max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 py-8 relative z-10">
        <nav className="text-xs text-gray-500 mb-4 dz-fade-up" style={{ animationDelay: '0ms' }}>
          <Link to="/" className="hover:text-gray-700 transition-colors">
            Home
          </Link>{' '}
          <span className="mx-1 text-gray-300">/</span>{' '}
          <span
            className="font-medium bg-clip-text text-transparent"
            style={{ backgroundImage: `linear-gradient(to right, ${BRAND}, #e8720c)` }}
          >
            Contact Us
          </span>
        </nav>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          {/* Info panel */}
          <div className="order-2 md:order-1 dz-fade-up" style={{ animationDelay: '80ms' }}>
            <div
              className="h-full rounded-3xl overflow-hidden p-8 flex flex-col justify-center shadow-md border relative"
              style={{
                backgroundImage: 'linear-gradient(135deg, #fff3e0, #fff0f5)',
                borderColor: '#ffe3b3',
              }}
            >
              <div
                className="dz-blob-2 pointer-events-none absolute -right-10 -bottom-10 w-40 h-40 rounded-full opacity-30 blur-2xl"
                style={{ backgroundImage: `linear-gradient(135deg, ${BRAND}, #e8720c)` }}
              />
              <div className="max-w-md relative">
                <h2
                  className="text-3xl md:text-4xl font-extrabold mb-2 bg-clip-text text-transparent"
                  style={{ backgroundImage: `linear-gradient(to right, ${BRAND}, #e8720c)` }}
                >
                  We're here to help
                </h2>
                <p className="text-sm text-gray-700 mb-6">
                  Have a question about an order, product, or partnership? Send us a message and we'll reply within 24 hours.
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-3 group">
                    <div className="p-2 rounded-lg bg-white shadow-sm transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" style={{ color: BRAND }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v9a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Email</div>
                      <div className="text-sm text-gray-800 font-medium">dehleze.pk@gmail.com</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 group">
                    <div className="p-2 rounded-lg bg-white shadow-sm transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" style={{ color: BRAND }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m3 4l3 3m0 0l-3 3m3-3H3" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Phone</div>
                      <div className="text-sm text-gray-800 font-medium">+923224540783</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form panel */}
          <div className="order-1 md:order-2 dz-fade-up" style={{ animationDelay: '160ms' }}>
            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 sm:p-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-1">Contact Us for Any Questions</h1>
              <p className="text-sm text-gray-600 mb-4">Fill out the form and we'll get back to you shortly.</p>

              {success && (
                <div className="dz-pop-in rounded-lg bg-green-50 border border-green-100 p-4 text-sm text-green-800 mb-4">
                  {success}
                </div>
              )}

              <form className="space-y-4" onSubmit={handleSubmit} noValidate>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600">First name</label>
                    <input
                      value={form.firstName}
                      onChange={(ev) => setForm({ ...form, firstName: ev.target.value })}
                      type="text"
                      className="dz-input w-full rounded-full border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-black focus:outline-none focus:ring-2 focus:shadow-md"
                      style={{ ['--tw-ring-color' as any]: `${BRAND}55` }}
                      onFocus={(ev) => (ev.currentTarget.style.borderColor = BRAND)}
                      onBlur={(ev) => (ev.currentTarget.style.borderColor = '')}
                    />
                    {errors.firstName && <div className="text-rose-600 text-xs dz-pop-in">{errors.firstName}</div>}
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600">Last name</label>
                    <input
                      value={form.lastName}
                      onChange={(ev) => setForm({ ...form, lastName: ev.target.value })}
                      type="text"
                      className="dz-input w-full rounded-full border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-black focus:outline-none focus:ring-2"
                      style={{ ['--tw-ring-color' as any]: `${BRAND}55` }}
                      onFocus={(ev) => (ev.currentTarget.style.borderColor = BRAND)}
                      onBlur={(ev) => (ev.currentTarget.style.borderColor = '')}
                    />
                    {errors.lastName && <div className="text-rose-600 text-xs dz-pop-in">{errors.lastName}</div>}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600">Phone number</label>
                    <input
                      value={form.phone}
                      onChange={(ev) => setForm({ ...form, phone: ev.target.value })}
                      type="tel"
                      className="dz-input w-full rounded-full border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-black focus:outline-none focus:ring-2"
                      style={{ ['--tw-ring-color' as any]: `${BRAND}55` }}
                      onFocus={(ev) => (ev.currentTarget.style.borderColor = BRAND)}
                      onBlur={(ev) => (ev.currentTarget.style.borderColor = '')}
                    />
                    {errors.phone && <div className="text-rose-600 text-xs dz-pop-in">{errors.phone}</div>}
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600">Email</label>
                    <input
                      value={form.email}
                      onChange={(ev) => setForm({ ...form, email: ev.target.value })}
                      type="email"
                      className="dz-input w-full rounded-full border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-black focus:outline-none focus:ring-2"
                      style={{ ['--tw-ring-color' as any]: `${BRAND}55` }}
                      onFocus={(ev) => (ev.currentTarget.style.borderColor = BRAND)}
                      onBlur={(ev) => (ev.currentTarget.style.borderColor = '')}
                    />
                    {errors.email && <div className="text-rose-600 text-xs dz-pop-in">{errors.email}</div>}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-600">Your message</label>
                  <textarea
                    rows={6}
                    value={form.message}
                    onChange={(ev) => setForm({ ...form, message: ev.target.value })}
                    className="dz-input w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-black resize-none focus:outline-none focus:ring-2"
                    style={{ ['--tw-ring-color' as any]: `${BRAND}55` }}
                    onFocus={(ev) => (ev.currentTarget.style.borderColor = BRAND)}
                    onBlur={(ev) => (ev.currentTarget.style.borderColor = '')}
                  />
                  {errors.message && <div className="text-rose-600 text-xs dz-pop-in">{errors.message}</div>}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="dz-shimmer-btn mt-2 w-full inline-flex justify-center items-center gap-2 px-6 py-3 rounded-full text-white text-sm font-semibold shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-70 disabled:hover:scale-100"
                  style={{
                    backgroundImage: `linear-gradient(to right, #ffb74d, ${BRAND}, #e8720c, ${BRAND}, #ffb74d)`,
                    boxShadow: '0 10px 25px -8px rgba(253,158,16,0.45)',
                  }}
                >
                  {isSubmitting && (
                    <svg className="dz-spinner h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="9" stroke="white" strokeOpacity="0.3" strokeWidth="3" />
                      <path d="M21 12a9 9 0 00-9-9" stroke="white" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                  )}
                  {isSubmitting ? 'Sending...' : 'Send message'}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}